import { Injectable } from '@angular/core';
import { FormShareService } from './share.service';
import { FormDataService } from './data.service';
import { SwalService } from '../../fastlane-common/services/swal.service';
import { FormStatusService } from './status.service';
import { FormEmailService } from './email.service';
import { FormFeeService } from './fee.service';
import { User } from '../../user/models/user.model';
import {formTypes} from '../../form/form.constants';
import { mergeMap } from 'rxjs/operators';
import { shouldSkipAllEmailNotifications } from '../../email/email.functions';
import { formStatus, formStatuses } from '../form.constants';
import { showApplicationRedirectIfApplicable } from '../../project/project.functions';
import { NavService } from '../../fastlane-common/services/nav.service';
import { Subscription, Observable } from '../../../../node_modules/rxjs';
import { PublishSubscribeService } from '../../fastlane-common/services/publish-subscribe.service';
import {
  publishSubscribeEventStrings,
  events
} from '../../fastlane-common/event/event.constants';
import { nextTick } from 'q';
import {
  incentiveCategory,
  auditDepositTransactionActions,
  incentiveProgram, paymentCompleted
} from '../../project/project.constants';
import { EmailService } from '../../email/services/email.service';
import { userType } from '../../user/user.constants';
import {FormIdentifier} from '../../project/models/form-identifier.model';
import {ResolvedProject} from '../../project/services/project-data.service';
import {ProjectBase} from '../../project/models/project-base.model';
import {ProjectDetailService} from '../../project/services/project-shared.service';
import {nullUser} from '../../user/models/nullUser.model';
import {UserContextService} from '../../user/services/user-context.service';

@Injectable()
export class FormConfirmationService {
  constructor(
    private dataService: FormDataService,
    private emailService: FormEmailService,
    private eventEmailService: EmailService,
    private feeService: FormFeeService,
    private projectDetailService: ProjectDetailService,
    private shareService: FormShareService,
    private statusService: FormStatusService,
    private swal: SwalService,
    private navService: NavService,
    private pubSubService: PublishSubscribeService,
    private userContext: UserContextService
  ) {}

  processConfirmation(): Promise<any> {
    const that = this;
    const newPayment = this.shareService.formNewPayment;
    return new Promise((resolve, reject) => {
      that.swal.load(
        'Validating your confirmation...',
        'Please keep this website open while we process your transaction.'
      );
      if (!that.validateConfirmation(newPayment)) {
        reject('The confirmation data we received is invalid.');
      } else {
        that.swal.load(
          'Logging your confirmation data...',
          'Please keep this website open while we process your transaction.'
        );
        that.dataService
          .registerConfirmation(newPayment)
          .then(() => {
            that.swal.load(
              'Additional processing...',
              'Please keep this website open while we process your transaction.'
            );

            const form = that.shareService.form;
            if (!form.payments) {
              form.payments = [newPayment.id];
            } else {
              form.payments.push(newPayment.id);
            }

            that.feeService.recalculateFees();

            // Decide the right status depending on the circumstance.
            // At this point, there are only 2 situations. Deposit Received (for Entertainment Audit) vs Received
            // This can be easily accomplished by simply checking the isRefundable property of the feeCalculation given
            // that the audit is the only one setting it for now.
            // NOTE: Audit is only form which will have pending payment as the first status and there will be nothing in statuses[1]

            that.swal.load(
              'Updating form details..',
              'Please keep this website open while we process your transaction.'
            );

            this.changeStatus().add(() => {
              that.dataService
                .saveForm()
                .pipe(
                  mergeMap(() => {
                  if (
                    that.shareService.form.type !== formTypes.audit.abbrev && that.shareService.form.type !== formTypes.evr.abbrev
                    ) {
                      return that.dataService.takeFormSnapShot();
                    } else {
                      return new Observable(subscriber => {
                        subscriber.next();
                      });
                    }
                  })
                )
                .subscribe(
                  () => {
                    that.pubSubService.publish(
                      publishSubscribeEventStrings.amountDueChanged
                    );
                    // TODO: Move this email logic to event handlers in email service.
                    // Determine if the email should be sent.
                    if (
                      shouldSkipAllEmailNotifications(
                        that.shareService.project.projectInfo,
                        that.shareService.form.type,
                        that.shareService.form.statuses[0].status
                      )
                    ) {
                      let text = 'Your Deposit is Received and will be reviewed by an LED manager.';
                      if (this.shareService.currentIncentiveProgram === incentiveProgram.film.code) {
                        if (this.shareService.filmForm.sourceFormId.formType == formTypes.application.abbrev)
                          text = 'Your deposit is submitted and will be reviewed by an LED manager after both the audit deposit and the application fee have been submitted.';
                      }
                      else if (this.shareService.currentIncentiveProgram === incentiveProgram.dm.code) {
                        if (this.shareService.digitalForm.sourceFormId.formType == formTypes.application.abbrev)
                          text = 'Your deposit is submitted and will be reviewed by an LED manager after both the audit deposit and the application fee have been submitted.';
                      }
                      that.swal
                        .success({
                          title: 'Payment Submitted!',
                          text:
                            text
                        })
                        .then(() => {
                            showApplicationRedirectIfApplicable(
                              that.shareService.filmForm.statuses[0].status,
                              that.shareService.filmForm.sourceFormId,
                              that.swal,
                              that.navService
                            )
                            .then(() => resolve())
                            .catch(() => resolve());
                        })
                        .catch(() => resolve());
                    } else if ( // Send notifications if the form type is not an audit with index 0 with status of *deposit received*
                      !(
                        (
                          this.shareService.form.type === formTypes.evr.abbrev ||
                          this.shareService.form.type === formTypes.audit.abbrev
                        )
                        &&
                        this.shareService.form.formIndex === 0 &&
                        (
                          !this.shareService.form.statuses ||
                          this.shareService.form.statuses[0].status === formStatus.depositReceived ||
                          this.shareService.form.statuses[0].status === formStatus.received ||
                          this.shareService.form.statuses.length < 4
                        )
                      )
                    ) {
                      that.swal.load(
                        'Notifying project contacts...',
                        'Please keep this website open while we process your transaction.'
                      );
                      let ccList = new Array<User>();
                      // get assessor for the current project parish.
                      this.shareService.getFormAssessors().subscribe(cc => {
                        // Filters the assessor who opted for the program which matches current project incentive program.
                        const assessorList = cc.filter(
                          user =>
                            user.incentivePrograms.indexOf(
                              that.shareService.project.projectInfo
                                .incentiveProgram
                            ) >= 0
                        );
                        // CC assessors only for the following form Types.
                        if (
                          (
                            this.shareService.form.type === formTypes.advance.abbrev ||
                            this.shareService.form.type === formTypes.application.abbrev ||
                            this.shareService.form.type === formTypes.projectApplication.abbrev ||
                            this.shareService.form.type === formTypes.afc.abbrev ||
                            this.shareService.form.type === formTypes.pcr.abbrev
                          )
                          &&
                          assessorList.length > 0
                        ) {
                          assessorList.forEach(assessor => {
                            ccList.push(assessor);
                          });
                        }
                        if (
                          incentiveCategory.entertainment.incentivePrograms.includes(
                            this.shareService.currentIncentiveProgram
                          )
                        ) {
                          this.eventEmailService
                            .getCarbonCopyUsersWithEmailPreferences(
                              [
                                <User>{
                                  userType: userType.management.code
                                }
                              ],
                              that.shareService.projectBase.projectInfo.location
                                .parish,
                              events.formStatusChanges.code,
                              formStatuses.received.name,
                              that.shareService.projectBase.projectInfo
                            )
                            .subscribe(managers => {
                              const managerUsers = managers.map(m =>
                                Object.assign(new User(), m)
                              );
                              ccList.push(...managerUsers);
                              // Send Payment received email to the applicant and CC manager and assessors.
                              this.eventEmailService.getCarbonCopyUsersWithEmailPreferences(
                                [
                                  <User>{
                                    userType: userType.management.code
                                  }
                                ],
                                that.shareService.projectBase.projectInfo
                                  .location.parish,
                                events.formStatusChanges.code,
                                formStatuses.received.name,
                                that.shareService.projectBase.projectInfo
                              );
                              that.emailService
                                .sendSubmissionSuccessfulEmail(ccList)
                                .subscribe(
                                  () => {
                                    that.swal
                                      .success({
                                        title: 'Payment Submitted!',
                                        text:
                                          'Your form is now "Received" and will be reviewed by an LED manager.'
                                      })
                                      .then(() => resolve())
                                      .catch(() => resolve());
                                  },
                                  () => {
                                    reject(
                                      'An error occurred while emailing project contacts.'
                                    );
                                  }
                                );
                            });
                        } else {
                          // Add program managers to the CC List.
                          this.shareService
                            .getProgramManager()
                            .subscribe(manager => {
                              ccList.push(manager);
                              // If the project is confidential, clear the ccList.
                              // The whole implementation has to be redone with a bit more time
                              // to allow a situation where we do not even go to get CCs or managers from the api.
                              // This would make it more efficient,
                              // but we do not have time to do this now.
                              if (
                                that.shareService.project.projectInfo
                                  .isConfidential
                              ) {
                                ccList = [];
                              }
                              // Send Payment received email to the applicant and CC manager and assessors.
                              that.emailService
                                .sendSubmissionSuccessfulEmail(ccList)
                                .subscribe(
                                  () => {
                                    that.swal
                                      .success({
                                        title: 'Payment Submitted!',
                                        text:
                                          'Your form is now "Received" and will be reviewed by an LED manager.'
                                      })
                                      .then(() => resolve())
                                      .catch(() => resolve());
                                  },
                                  () => {
                                    reject(
                                      'An error occurred while emailing project contacts.'
                                    );
                                  }
                                );
                            });
                        }
                      });
                    } else {
                      // If the payment is done for additional  on the audit
                      if (this.shareService.form.payments.length > 1) {
                        this.eventEmailService
                          .getCarbonCopyUsersWithEmailPreferences(
                            [
                              <User>{
                                userType: userType.management.code
                              }
                            ],
                            that.shareService.projectBase.projectInfo.location
                              .parish,
                            events.auditDepositTransaction.code,
                            auditDepositTransactionActions.requestPayment,
                            that.shareService.projectBase.projectInfo
                          )
                          .subscribe(managers => {
                            that.emailService
                              .sendAdditionalPaymentReceivedEmail(managers)
                              .subscribe(
                                () => {
                                  that.swal
                                    .success({
                                      title: 'Payment Submitted!',
                                      text:
                                        'Notification has been sent to a LED manager.'
                                    })
                                    .then(() => resolve())
                                    .catch(() => resolve());
                                },
                                () => {
                                  reject(
                                    'An error occurred while emailing manager.'
                                  );
                                }
                              );
                          });
                      } else {
                        let text = 'Your Deposit is Received and will be reviewed by an LED manager.';
                        if (this.shareService.currentIncentiveProgram === incentiveProgram.film.code) {
                          if (this.shareService.filmForm.sourceFormId.formType == formTypes.application.abbrev)
                            text = 'Your deposit is submitted and will be reviewed by an LED manager after both the audit deposit and the application fee have been submitted.';
                        }
                        else if (this.shareService.currentIncentiveProgram === incentiveProgram.dm.code) {
                          if (this.shareService.digitalForm.sourceFormId.formType == formTypes.application.abbrev)
                            text = 'Your deposit is submitted and will be reviewed by an LED manager after both the audit deposit and the application fee have been submitted.';
                        }
                        that.swal
                          .success({
                            title: 'Payment Submitted!',
                            text:
                              text
                          })
                          .then(() => {
                              showApplicationRedirectIfApplicable(
                                that.shareService.filmForm.statuses[0].status,
                                that.shareService.filmForm.sourceFormId,
                                that.swal,
                                that.navService
                              )
                              .then(() => resolve())
                              .catch(() => resolve());
                          })
                          .catch(() => resolve());
                      }
                    }
                  },
                  () => {
                    reject('An error occurred while processing your form.');
                  }
                );
            });
          })
          .catch(reason => reject(reason));
      }
    });
  }

  processOrbipayConfirmation(): Promise<boolean> {
    const that = this;
    const newPayment = this.shareService.formNewPayment;
    return new Promise((resolve, reject) => {
      that.feeService.recalculateFees();

      // Decide the right status depending on the circumstance.
      // At this point, there are only 2 situations. Deposit Received (for Entertainment Audit) vs Received
      // This can be easily accomplished by simply checking the isRefundable property of the feeCalculation given
      // that the audit is the only one setting it for now.
      // NOTE: Audit is only form which will have pending payment as the first status and there will be nothing in statuses[1]

      this.changeStatus().add(() => {

        that.dataService
          .saveForm()
          .pipe(
          mergeMap(() => {
            if (
              that.shareService.form.type !== formTypes.audit.abbrev &&
              this.shareService.form.type !== formTypes.evr.abbrev
            ) {
                return that.dataService.takeFormSnapShot();
              } else {
                return new Observable(subscriber => {
                  subscriber.next();
                });
              }
            })
          )
          .subscribe(
            () => {
              that.continueConfirmation(that, resolve, reject);
              resolve();
            },
            () => {
              that.continueConfirmation(that, resolve, reject);
              resolve();
            }
          );
      });
    });
  }

  continueConfirmation(
    that: any,
    resolve: (value?: boolean | PromiseLike<boolean>) => void,
    reject: (reason?: any) => void
  ) {
    that.pubSubService.publish(publishSubscribeEventStrings.amountDueChanged);
    // TODO: Move this email logic to event handlers in email service.
    // Determine if the email should be sent.
    if (
      shouldSkipAllEmailNotifications(
        that.shareService.project.projectInfo,
        that.shareService.form.type,
        that.shareService.form.statuses[0].status
      )
    ) {
      let text = 'Your Deposit is Received and will be reviewed by an LED manager.';
      if (this.shareService.currentIncentiveProgram === incentiveProgram.film.code) {
        if (this.shareService.filmForm.sourceFormId.formType == formTypes.application.abbrev)
          text = 'Your deposit is submitted and will be reviewed by an LED manager after both the audit deposit and the application fee have been submitted.';
      }
      else if (this.shareService.currentIncentiveProgram === incentiveProgram.dm.code) {
        if (this.shareService.digitalForm.sourceFormId.formType == formTypes.application.abbrev)
          text = 'Your deposit is submitted and will be reviewed by an LED manager after both the audit deposit and the application fee have been submitted.';
      }
      that.swal
        .success({
          title: 'Payment Submitted!',
          text:
            text
        })
        .then(() => {
          if (this.userContext.currentUser === nullUser &&
            this.shareService.project.projectInfo.incentiveProgram === incentiveProgram.prr.code &&
            this.shareService.form.type === formTypes.prr.abbrev) {
            this.pubSubService.raise(
              events.PaymentCompleted.code,
              paymentCompleted.paymentCompleted,
              {}
            );
            this.shareService.formId = {} as FormIdentifier;
            this.emailService.sendPrrPaymentEmail(this.shareService.project.projectInfo.projectId).subscribe(() => {
              this.projectDetailService.setResolvedProject(<ResolvedProject>{
                payments: null,
                project: new ProjectBase().init(),
                projectDates: null
              });
            }, () => {
              this.projectDetailService.setResolvedProject(<ResolvedProject>{
                payments: null,
                project: new ProjectBase().init(),
                projectDates: null
              });
            });

          }
          showApplicationRedirectIfApplicable(
            that.shareService.filmForm.statuses[0].status,
            that.shareService.filmForm.sourceFormId,
            that.swal,
            that.navService
          )
            .then(() => resolve())
            .catch(() => resolve());
        })
        .catch(() => resolve());
      return;
    }

    // Send notifications if the form type is not an audit with index 0 with status of *deposit received*
    if (
      !((this.shareService.form.type === formTypes.evr.abbrev ||
        this.shareService.form.type === formTypes.audit.abbrev) &&
        this.shareService.form.formIndex === 0 &&
        (!this.shareService.form.statuses ||
          this.shareService.form.statuses[0].status === formStatus.depositReceived ||
          this.shareService.form.statuses[0].status === formStatus.received ||
          this.shareService.form.statuses.length < 4)
      )
    ) {
      that.swal.load(
        'Notifying project contacts...',
        'Please keep this website open while we process your transaction.'
      );
      let ccList = new Array<User>();
      // get assessor for the current project parish.
      this.shareService.getFormAssessors().subscribe(cc => {
        // Filters the assessor who opted for the program which matches current project incentive program.
        const assessorList = cc.filter(
          user =>
            user.incentivePrograms.indexOf(
              that.shareService.project.projectInfo.incentiveProgram
            ) >= 0
        );
        // CC assessors only for the following form Types.
        if (
          (this.shareService.form.type === formTypes.advance.abbrev ||
            this.shareService.form.type === formTypes.application.abbrev ||
            this.shareService.form.type === formTypes.projectApplication.abbrev ||
            this.shareService.form.type === formTypes.afc.abbrev ||
            this.shareService.form.type === formTypes.pcr.abbrev) &&
          assessorList.length > 0
        ) {
          assessorList.forEach(assessor => {
            ccList.push(assessor);
          });
        }
        if (
          incentiveCategory.entertainment.incentivePrograms.includes(
            this.shareService.currentIncentiveProgram
          )
        ) {
          this.eventEmailService
            .getCarbonCopyUsersWithEmailPreferences(
              [
                <User>{
                  userType: userType.management.code
                }
              ],
              that.shareService.projectBase.projectInfo.location.parish,
              events.formStatusChanges.code,
              formStatuses.received.name,
              that.shareService.projectBase.projectInfo
            )
            .subscribe(managers => {
              const managerUsers = managers.map(m =>
                Object.assign(new User(), m)
              );
              ccList.push(...managerUsers);
              // Send Payment received email to the applicant and CC manager and assessors.
              this.eventEmailService.getCarbonCopyUsersWithEmailPreferences(
                [
                  <User>{
                    userType: userType.management.code
                  }
                ],
                that.shareService.projectBase.projectInfo.location.parish,
                events.formStatusChanges.code,
                formStatuses.received.name,
                that.shareService.projectBase.projectInfo
              );

              if (this.shareService.form.type === formTypes.evr.abbrev) {
                that.swal
                  .success({
                    title: 'Payment Submitted!',
                    text:
                      'Your form is now "Received" and will be reviewed by an LED manager.'
                  })
                  .then(() => {
                    showApplicationRedirectIfApplicable(
                      that.shareService.filmForm.statuses[0].status,
                      that.shareService.filmForm.sourceFormId,
                      that.swal,
                      that.navService
                    )
                      .then(() => resolve())
                      .catch(() => resolve());
                  })
                  .catch(() => resolve());
              }
              else {
                that.emailService.sendSubmissionSuccessfulEmail(ccList, {isPayment: true}).subscribe(
                  () => {
                    that.swal
                      .success({
                        title: 'Payment Submitted!',
                        text:
                          'Your form is now "Received" and will be reviewed by an LED manager.'
                      })
                      .then(() => {
                        showApplicationRedirectIfApplicable(
                          that.shareService.filmForm.statuses[0].status,
                          that.shareService.filmForm.sourceFormId,
                          that.swal,
                          that.navService
                        )
                          .then(() => resolve())
                          .catch(() => resolve());
                      })
                      .catch(() => resolve());
                  },
                  () => {
                    reject('An error occurred while emailing project contacts.');
                  }
                );
              }



            });
        } else {
          // Add program managers to the CC List.
          this.shareService.getProgramManager().subscribe(manager => {
            ccList.push(manager);
            // If the project is confidential, clear the ccList.
            // The whole implementation has to be redone with a bit more time
            // to allow a situation where we do not even go to get CCs or managers from the api.
            // This would make it more efficient,
            // but we do not have time to do this now.
            if (that.shareService.project.projectInfo.isConfidential) {
              ccList = [];
            }

            // Send Payment received email to the applicant and CC manager and assessors.
            that.emailService.sendSubmissionSuccessfulEmail(ccList).subscribe(
              () => {
                that.swal
                  .success({
                    title: 'Payment Submitted!',
                    text:
                      'Your form is now "Received" and will be reviewed by an LED manager.'
                  })
                  .then(() => resolve())
                  .catch(() => resolve());
              },
              () => {
                reject('An error occurred while emailing project contacts.');
              }
            );
          });
        }
      });
    }
    else {
      console.log(this.shareService.form);
      // If the payment is done for additional  on the audit
      if (this.shareService.form.payments.length > 1) {
        this.eventEmailService
          .getCarbonCopyUsersWithEmailPreferences(
            [
              <User>{
                userType: userType.management.code
              }
            ],
            that.shareService.projectBase.projectInfo.location.parish,
            events.auditDepositTransaction.code,
            auditDepositTransactionActions.requestPayment,
            that.shareService.projectBase.projectInfo
          )
          .subscribe(managers => {
            that.emailService
              .sendAdditionalPaymentReceivedEmail(managers)
              .subscribe(
                () => {
                  that.swal
                    .success({
                      title: 'Payment Submitted!',
                      text: 'Notification has been sent to a LED manager.'
                    })
                    .then(() => resolve())
                    .catch(() => resolve());
                },
                () => {
                  reject('An error occurred while emailing manager.');
                }
              );
          });
      }
      else {
        that.shareService.refreshPage();
        let text = 'Your Deposit is Received and will be reviewed by an LED manager.';
        if (this.shareService.currentIncentiveProgram === incentiveProgram.film.code) {
          if (this.shareService.filmForm.sourceFormId.formType == formTypes.application.abbrev)
            text = 'Your deposit is submitted and will be reviewed by an LED manager after both the audit deposit and the application fee have been submitted.';
        }
        else if (this.shareService.currentIncentiveProgram === incentiveProgram.dm.code) {
          if (this.shareService.digitalForm.sourceFormId.formType == formTypes.application.abbrev)
            text = 'Your deposit is submitted and will be reviewed by an LED manager after both the audit deposit and the application fee have been submitted.';
        }
        that.swal
          .success({
            title: 'Payment Submitted!',
            text:
              text
          })
          .then(() => {
            showApplicationRedirectIfApplicable(
              that.shareService.filmForm.statuses[0].status,
              that.shareService.filmForm.sourceFormId,
              that.swal,
              that.navService
            )
              .then(() => resolve())
              .catch(() => resolve());
          })

          .catch(() => resolve());
        return;
      }
    }
  }

  changeStatus(): Subscription {
    const form = this.shareService.form;
    let subscription: Subscription;

    const currentStatus = form.statuses[0].status;
    const isCurrentStatusPendingPayment = currentStatus === formStatus.pendingPayment;

    let previousDifferentStatusIndex = 1;
    while ( form.statuses.length > 1 &&
            form.statuses.length > previousDifferentStatusIndex &&
            currentStatus === form.statuses[previousDifferentStatusIndex].status ) {
      previousDifferentStatusIndex++;
    }
    if (
      form.statuses.length > 1 &&
      this.resetToPreviousStatus.includes(form.statuses[previousDifferentStatusIndex].status)
    ) {
      // LED-609 If the user is returning a form to a previous status, the current status is payment pending,
      // and the new status is awaiting audit, there should be an additional status of depositReceived
      if (
        (
          form.statuses[previousDifferentStatusIndex].status === formStatus.auditorAssigned ||
          form.statuses[previousDifferentStatusIndex].status === formStatus.awaitingAudit ||
          form.statuses[previousDifferentStatusIndex].status === formStatus.received
        )
        && isCurrentStatusPendingPayment
      ) {
        this.statusService.changeStatus(formStatus.depositReceived);
        previousDifferentStatusIndex++;
      }
      subscription = this.statusService.changeStatus(form.statuses[previousDifferentStatusIndex].status);
    } else if (form.fees[0].isRefundable) {
      subscription = this.statusService.changeStatus(
        formStatus.depositReceived
      );
    } else {
      if (isCurrentStatusPendingPayment) {
        this.statusService.changeStatus(formStatus.depositReceived);
      }
      subscription = this.statusService.changeStatus(formStatus.received);
    }
    return subscription;
  }

  private validateConfirmation(payment) {
    return !(
      payment.returnCode === undefined ||
      payment.returnCode == null ||
      payment.confirmationNumber === undefined ||
      payment.confirmationNumber == null ||
      payment.confirmationNumber === ''
    );
  }

  /**
   * @summary Use this constant to figure out if the next status after successful payment
   * should be the previous status as indicated by fees[1]
   */
  private get resetToPreviousStatus() {
    return [
      formStatus.auditorAssigned,
      formStatus.awaitingAudit,
      formStatus.received
    ];
  }
}
